import { Badge, Button, Field, Input, PasswordInput, Select } from '@aibi/ui';
import type { AzureSettings } from '../services/ai-settings-api';
import { withAzureTarget } from './azure-ai-form';
import { Notice } from './ErrorNotice';
import type { AzureForm } from './form-rules';

type Errors = Partial<Record<string, string>>;

const AUTH_OPTIONS = [
  { value: 'api-key', label: 'Clave de API del recurso' },
  { value: 'entra-sp', label: 'Service principal de Entra ID' },
] as const;

const API_OPTIONS = [
  {
    value: 'chat-completions',
    label: 'Chat Completions (GPT, DeepSeek, Llama, Mistral, Phi, Grok)',
  },
  { value: 'anthropic-messages', label: 'Claude (API Messages de Anthropic)' },
] as const;

/**
 * Campos de Azure AI Foundry. El endpoint y la credencial se envian una vez al servidor,
 * que los guarda cifrados; solo el estado del formulario los contiene.
 */
export function AzureAiFields({
  form,
  saved,
  readOnly,
  replaceEndpoint,
  secretRequired,
  errors,
  onChange,
  onReplaceEndpoint,
}: {
  readonly form: AzureForm;
  readonly saved: AzureSettings | null;
  readonly readOnly: boolean;
  readonly replaceEndpoint: boolean;
  readonly secretRequired: boolean;
  readonly errors: Errors;
  readonly onChange: (form: AzureForm) => void;
  readonly onReplaceEndpoint: () => void;
}) {
  if (readOnly) {
    return (
      <p className="text-xs text-muted-foreground">
        Modelo <strong>{saved?.model ?? 'del endpoint'}</strong> en{' '}
        <strong>{saved?.host ?? 'host desconocido'}</strong>
        {saved?.api === 'anthropic-messages' ? ' (Claude)' : ''} con{' '}
        {saved?.authType === 'entra-sp' ? 'service principal de Entra ID' : 'clave de API'}.
      </p>
    );
  }
  const text =
    (key: 'tenantId' | 'clientId' | 'secret') => (event: { target: { value: string } }) => {
      onChange({ ...form, [key]: event.target.value });
    };
  const target = (key: 'endpoint' | 'model') => (event: { target: { value: string } }) => {
    onChange(withAzureTarget(form, { [key]: event.target.value }));
  };
  const sp = form.authType === 'entra-sp';
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        {saved?.endpointConfigured && !replaceEndpoint ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium">Endpoint</span>
              <Badge variant="success" className="self-start">
                Guardado ({saved.host ?? 'host oculto'})
              </Badge>
            </div>
            <Button type="button" size="sm" variant="ghost" onClick={onReplaceEndpoint}>
              Reemplazar
            </Button>
          </div>
        ) : (
          <Field
            label="Endpoint de Microsoft Foundry"
            hint='En la pagina de inicio de Foundry: "Punto de conexion del proyecto" o "Punto de conexion de Azure OpenAI". Tambien sirve el URI de destino del despliegue.'
            error={errors.endpoint}
          >
            {(control) => (
              <Input
                {...control}
                value={form.endpoint}
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                placeholder="https://mi-recurso.services.ai.azure.com"
                onChange={target('endpoint')}
              />
            )}
          </Field>
        )}
      </div>
      <Field
        label="Nombre del despliegue"
        hint="El nombre de la implementacion (Ver implementaciones), por ejemplo gpt-5.4-mini o el de su despliegue de Claude o DeepSeek."
        error={errors.model}
      >
        {(control) => (
          <Input
            {...control}
            value={form.model}
            autoComplete="off"
            spellCheck={false}
            onChange={target('model')}
          />
        )}
      </Field>
      <Field
        label="API del modelo"
        hint="Se elige sola con despliegues de Claude; puede cambiarla."
        error={errors.api}
      >
        {(control) => (
          <Select
            {...control}
            value={form.api}
            onChange={(event) => {
              onChange({
                ...form,
                api:
                  event.target.value === 'anthropic-messages'
                    ? 'anthropic-messages'
                    : 'chat-completions',
              });
            }}
          >
            {API_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Autenticacion" error={errors.authType}>
        {(control) => (
          <Select
            {...control}
            value={form.authType}
            onChange={(event) => {
              onChange({
                ...form,
                authType: event.target.value === 'entra-sp' ? 'entra-sp' : 'api-key',
              });
            }}
          >
            {AUTH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {sp && (
        <>
          <Field
            label="Tenant ID"
            error={errors.tenantId}
            hint="Directory (tenant) ID de Entra ID."
          >
            {(control) => (
              <Input
                {...control}
                value={form.tenantId}
                autoComplete="off"
                spellCheck={false}
                onChange={text('tenantId')}
              />
            )}
          </Field>
          <Field
            label="Client ID"
            error={errors.clientId}
            hint="Application (client) ID del service principal."
          >
            {(control) => (
              <Input
                {...control}
                value={form.clientId}
                autoComplete="off"
                spellCheck={false}
                onChange={text('clientId')}
              />
            )}
          </Field>
          <div className="sm:col-span-2">
            <Notice tone="info">
              Asigne al service principal el rol &quot;Cognitive Services User&quot; en el recurso
              de Azure AI Foundry.
            </Notice>
          </div>
        </>
      )}
      <div className="sm:col-span-2">
        <Field
          label={sp ? 'Secreto del service principal' : 'Clave de API'}
          error={errors.secret}
          hint={
            secretRequired
              ? 'Se guarda cifrada en el servidor y nunca se vuelve a mostrar.'
              : 'Guardada. Dejela vacia para conservarla.'
          }
        >
          {(control) => (
            <PasswordInput
              {...control}
              value={form.secret}
              placeholder={secretRequired ? '' : '********'}
              onChange={text('secret')}
            />
          )}
        </Field>
      </div>
    </div>
  );
}
